// src/composables/useAppVersion.js
//
// BUG-051 — descobrir que saiu build nova, sem depender de F5 manual.
//
// O QUE ESTAVA QUEBRADO. O service worker é registrado UMA vez, no `load` da
// página, e a versão vive na QUERY do registro (`sw.js?v=<sha>`), não nos bytes
// do arquivo. Numa SPA que a operação deixa aberta o turno inteiro não há
// navegação — então o browser nunca reavalia o SW, a URL registrada continua
// apontando para o SHA antigo, e a build nova simplesmente não é descoberta.
// Quando enfim era, o `controllerchange` recarregava a página em silêncio, no
// meio do que a pessoa estivesse fazendo. "Nunca atualiza, e quando atualiza é
// sem avisar".
//
// A SAÍDA. Tirar a descoberta do caminho do service worker: perguntar a um
// arquivo de versão gerado no build (`version.json`, emitido pelo vue.config.js
// junto do carimbo de `VUE_APP_VERSION`). Se a versão servida difere da que este
// bundle conhece, saiu deploy — e quem decide a hora de recarregar é o operador.

import { ref, onUnmounted } from "vue";
import { logger } from "@/utils/logger";

const POLL_MS = 5 * 60 * 1000;
// Quanto tempo o "Depois" segura o aviso. Curto de propósito: dispensar é
// "agora não", não "não quero a correção".
const SNOOZE_MS = 30 * 60 * 1000;

function versaoLocal() {
  return process.env.VUE_APP_VERSION || "";
}

export function useAppVersion({
  // Fora de produção o SW nem é registrado e o bundle não tem SHA — perguntar
  // seria ruído garantido.
  enabled = process.env.NODE_ENV === "production",
  pollMs = POLL_MS,
  snoozeMs = SNOOZE_MS,
} = {}) {
  const updateAvailable = ref(false);
  const remoteVersion = ref("");

  let timer = null;
  let silenciadoAte = 0;

  async function checkNow() {
    if (!enabled) return;
    const local = versaoLocal();
    // "dev" é o fallback de quem buildou sem SHA (ver vue.config.js): sem
    // identidade de build não há nada com que comparar.
    if (!local || local === "dev") return;

    try {
      const base = process.env.BASE_URL || "/";
      // `no-store` + cache-buster: `location /` do nginx manda `no-cache`, que é
      // revalidação, não ausência de cache. Um proxy no meio do caminho poderia
      // devolver a versão velha para sempre — e o aviso nunca apareceria.
      const resp = await fetch(`${base}version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!resp.ok) return;

      const { version } = (await resp.json()) || {};
      // Ausência de versão não é mudança de versão: sem isto, um deploy que
      // esquecesse de emitir o arquivo pediria reload a todo mundo, para sempre.
      if (!version) return;

      remoteVersion.value = version;
      if (version !== local && Date.now() >= silenciadoAte) {
        updateAvailable.value = true;
      }
    } catch (err) {
      // Silêncio deliberado: queda de rede é rotina numa tela que fica aberta o
      // dia todo, e não pode virar aviso de nova versão nem derrubar a tela.
      logger.debug("[app-version] verificação falhou (seguindo)", err);
    }
  }

  function start() {
    stop();
    if (!enabled) return;
    timer = setInterval(checkNow, pollMs);
    checkNow();
  }

  function stop() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function dismiss() {
    silenciadoAte = Date.now() + snoozeMs;
    updateAvailable.value = false;
  }

  async function applyUpdate() {
    // Best-effort: se o SW atualizar antes do reload, a página já sobe na build
    // nova de primeira. Se não, o reload resolve de qualquer forma — a navegação
    // é network-first, então o index.html vem fresco.
    try {
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
    } catch (err) {
      logger.debug("[app-version] update() do SW falhou (seguindo)", err);
    }
    window.location.reload();
  }

  function onVisibilityChange() {
    if (!document.hidden) checkNow();
  }

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  onUnmounted(() => {
    stop();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  });

  return { updateAvailable, remoteVersion, checkNow, start, stop, dismiss, applyUpdate };
}
